/**
 * Cross-domain authentication using cookies
 * Allows auth state to work across <root> and studio.<root> for every site
 * root (utils/siteRoots). Without the parent-domain scope (`.<root>`) a
 * cookie set on the root wouldn't be visible from studio.<root>, which is
 * exactly the host the post-OAuth CallbackPage hops to.
 */
import { siteRootOf } from './siteRoots';

const SESSION_COOKIE = 'zibby_session';
const USER_COOKIE = 'zibby_user';

const getCookieDomain = () => {
  const hostname = window.location.hostname;
  const root = siteRootOf(hostname);
  return root ? `.${root}` : null;
};

export const setCookie = (name, value, days = 365) => {
  const expires = new Date();
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
  
  const domain = getCookieDomain();
  const domainPart = domain ? `;domain=${domain}` : '';
  const securePart = window.location.protocol === 'https:' ? ';secure' : '';
  
  // Use base64 encoding to avoid cookie parsing issues with special chars
  const encoded = btoa(encodeURIComponent(value));
  document.cookie = `${name}=${encoded};expires=${expires.toUTCString()};path=/${domainPart}${securePart};samesite=lax`;
};

export const getCookie = (name) => {
  const nameEQ = name + '=';
  const ca = document.cookie.split(';');
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) {
      const encoded = c.substring(nameEQ.length, c.length);
      try {
        // Decode from base64
        const value = decodeURIComponent(atob(encoded));
        return value;
      } catch (e) {
        return null;
      }
    }
  }
  return null;
};

export const deleteCookie = (name) => {
  // Cookies set with two different scopes can BOTH be live for the
  // same name:
  //   1. parent-domain (".zibby.dev") — set by saveAuthToCookies, the
  //      intended cross-subdomain behavior
  //   2. host-only ("zibby.dev" or "studio.zibby.dev") — set by
  //      historical paths that didn't pass `domain=`, OR set by another
  //      tab on a specific subdomain
  //
  // Browsers send the MORE-SPECIFIC cookie first, so a leftover
  // host-only `zibby_session` for a previous account can shadow a
  // freshly-set parent-domain cookie. Logout looked like it worked
  // (parent cookie cleared) but reads kept returning the old session.
  //
  // Fix: clear BOTH scopes — host-only AND parent-domain — for every
  // delete. Belt + suspenders, no behavior change for the happy path.
  const expires = 'Thu, 01 Jan 1970 00:00:00 GMT';
  // 1. host-only (no domain attribute)
  document.cookie = `${name}=;expires=${expires};path=/`;
  // 2. parent-domain (e.g. .zibby.dev / .zibby.app)
  const parent = getCookieDomain();
  if (parent) {
    document.cookie = `${name}=;expires=${expires};path=/;domain=${parent}`;
  }
  // 3. exact hostname (in case of a host-only cookie with explicit domain)
  if (typeof window !== 'undefined' && window.location?.hostname) {
    document.cookie = `${name}=;expires=${expires};path=/;domain=${window.location.hostname}`;
  }
};

export const saveAuthToCookies = (session, user) => {
  if (session) {
    setCookie(SESSION_COOKIE, session);
  }
  if (user) {
    const essentialUser = {
      id: user.id,
      account_id: user.account_id,
      email: user.email,
      name: user.name,
      provider: user.provider
    };
    const userStr = JSON.stringify(essentialUser);
    setCookie(USER_COOKIE, userStr);
  }
};

export const getAuthFromCookies = () => {
  const session = getCookie(SESSION_COOKIE);
  const userStr = getCookie(USER_COOKIE);
  let user = null;
  if (userStr) {
    try {
      user = JSON.parse(userStr);
    } catch (e) {
      console.error('❌ Failed to parse user cookie:', e.message, userStr?.substring(0, 100));
    }
  }
  return { session, user };
};

export const clearAuthCookies = () => {
  deleteCookie(SESSION_COOKIE);
  deleteCookie(USER_COOKIE);
};
